import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Gauge,
  Settings,
  Shield,
  Clock,
  ChevronRight,
  CheckCircle2,
  AlertTriangle,
  BarChart3,
  Users,
  GaugeIcon
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const Operation = () => {
  const [activeTab, setActiveTab] = useState('monitoring');
  const navigate = useNavigate();
  const services = [
    {
      id: 'monitoring',
      title: 'Real-Time Monitoring',
      icon: <Gauge className="w-6 h-6" />,
      description: 'Advanced 24/7 boiler performance monitoring and analytics'
    },
    {
      id: 'maintenance',
      title: 'Preventive Maintenance',
      icon: <Settings className="w-6 h-6" />,
      description: 'Scheduled maintenance to prevent unexpected downtime'
    },
    {
      id: 'emergency',
      title: 'Emergency Response',
      icon: <AlertTriangle className="w-6 h-6" />,
      description: '24/7 emergency support and rapid response team'
    }
  ];

  const stats = [
    { value: '99.9%', label: 'Uptime Guarantee' },
    { value: '24/7', label: 'Support Available' },
    { value: '15+', label: 'Years Experience' },
    { value: '500+', label: 'Projects Completed' }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <motion.section 
        className="relative min-h-[400px] bg-gradient-to-r from-emerald-900 to-emerald-700 text-white overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-black opacity-30"></div>
        <div className="container mx-auto px-6 h-full flex flex-col justify-center relative z-10 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Expert Boiler Operations
            </h1>
            <p className="text-lg md:text-xl mb-6 max-w-2xl">
              Maximize efficiency and minimize downtime with our comprehensive boiler operation services
            </p>
            <motion.div
              className="flex flex-wrap gap-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              <button className="bg-white text-emerald-900 px-6 py-2.5 rounded-lg font-semibold hover:bg-emerald-50 transition-colors duration-300 flex items-center"
               onClick={() => navigate('/contact')}>
                Schedule Consultation
                <ChevronRight className="ml-2 w-5 h-5" />
              </button>
              
            </motion.div>
          </motion.div>
        </div>

        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-0 right-0 w-72 h-72 bg-emerald-500 rounded-full filter blur-3xl opacity-20"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </motion.section>

      {/* Stats Section */}
      <motion.section 
        className="py-16 bg-white"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center"
              >
                <h3 className="text-4xl font-bold text-emerald-600 mb-2">{stat.value}</h3>
                <p className="text-gray-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Services Tabs */}
      <section className="py-16 px-6">
        <div className="container mx-auto">
          <motion.div
            className="flex flex-wrap gap-4 justify-center mb-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
          >
            {services.map((service) => (
              <motion.button
                key={service.id}
                variants={itemVariants}
                className={`px-6 py-3 rounded-lg flex items-center gap-2 transition-all duration-300 ${
                  activeTab === service.id
                    ? 'bg-emerald-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => setActiveTab(service.id)}
              >
                {service.icon}
                {service.title}
              </motion.button>
            ))}
          </motion.div>

          <motion.div
            className="bg-white rounded-2xl shadow-xl p-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {services.map((service) => (
              <div
                key={service.id}
                className={`${activeTab === service.id ? 'block' : 'hidden'}`}
              >
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[1, 2, 3].map((item) => (
                    <motion.div
                      key={item}
                      className="bg-gray-50 p-6 rounded-lg"
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    >
                      <CheckCircle2 className="w-8 h-8 text-emerald-600 mb-4" />
                      <h4 className="text-lg font-semibold mb-2">Feature {item}</h4>
                      <p className="text-gray-600">Detailed description of the specific feature or benefit related to {service.title}.</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="container mx-auto">
          <motion.h2
            className="text-3xl font-bold text-center mb-12"
            variants={itemVariants}
            initial="hidden"
            whileInView="visible"
          >
            Our Operation Process
          </motion.h2>
          <div className="max-w-4xl mx-auto">
            {[
              { icon: <GaugeIcon />, title: 'Initial Assessment', description: 'Comprehensive evaluation of your boiler system' },
              { icon: <BarChart3 />, title: 'Performance Optimization', description: 'Fine-tuning for maximum efficiency' },
              { icon: <Users />, title: 'Team Training', description: 'Expert training for your operational staff' },
              { icon: <Clock />, title: 'Ongoing Support', description: '24/7 monitoring and maintenance' }
            ].map((step, index) => (
              <motion.div
                key={index}
                className="flex items-start mb-8 relative"
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
              >
                <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                  {step.icon}
                </div>
                <div className="ml-6">
                  <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      
    </div>
  );
};

export default Operation;